/**
 * NativeLocaleProvider (React Native)
 *
 * React context provider for locale management in React Native applications.
 * Wraps the app with I18nextProvider and provides locale state management.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { I18nextProvider } from 'react-i18next';

import {
  changeLocaleNative,
  i18n,
  initLocaleNative,
  setAsyncStorageAdapter,
  setDeviceLocaleAdapter,
  type IAsyncStorage,
  type IDeviceLocale,
} from '../config';
import {
  DEFAULT_LOCALE,
  type LocaleConfig,
  type LocaleContextType,
  type SupportedLocale,
} from '../types';

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT
// ═══════════════════════════════════════════════════════════════════════════

const NativeLocaleContext = createContext<LocaleContextType | null>(null);

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER PROPS
// ═══════════════════════════════════════════════════════════════════════════

export interface NativeLocaleProviderProps extends LocaleConfig {
  /** Child components */
  children: ReactNode;
  /** AsyncStorage adapter (required for persistence) */
  asyncStorage?: IAsyncStorage;
  /** Device locale adapter (required for detection) */
  deviceLocale?: IDeviceLocale;
}

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * NativeLocaleProvider component for React Native applications
 *
 * @example
 * ```tsx
 * import AsyncStorage from '@react-native-async-storage/async-storage';
 * import * as Localization from 'expo-localization';
 *
 * <NativeLocaleProvider
 *   asyncStorage={AsyncStorage}
 *   deviceLocale={{ getLocale: () => Localization.locale }}
 *   detectLanguage
 * >
 *   <App />
 * </NativeLocaleProvider>
 * ```
 */
export function NativeLocaleProvider({
  children,
  initialLocale,
  detectLanguage = true,
  debug = false,
  asyncStorage,
  deviceLocale,
}: NativeLocaleProviderProps) {
  const [locale, setLocaleState] = useState<SupportedLocale>(initialLocale || DEFAULT_LOCALE);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize i18n on mount
  useEffect(() => {
    let mounted = true;

    async function init() {
      try {
        // Set up adapters before initialization
        if (asyncStorage) {
          setAsyncStorageAdapter(asyncStorage);
        }
        if (deviceLocale) {
          setDeviceLocaleAdapter(deviceLocale);
        }

        await initLocaleNative({ initialLocale, detectLanguage, debug });

        if (mounted) {
          // Sync state with i18n
          setLocaleState(i18n.language as SupportedLocale);
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('[NativeLocaleProvider] Failed to initialize:', error);
        if (mounted) {
          setIsInitialized(true);
        }
      }
    }

    init();

    return () => {
      mounted = false;
    };
  }, [initialLocale, detectLanguage, debug, asyncStorage, deviceLocale]);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setLocaleState(lng as SupportedLocale);
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  // Change locale handler
  const setLocale = useCallback(async (newLocale: SupportedLocale) => {
    setIsLoading(true);
    try {
      await changeLocaleNative(newLocale);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Context value
  const value = useMemo<LocaleContextType>(
    () => ({
      locale,
      isLoading,
      isInitialized,
      setLocale,
    }),
    [locale, isLoading, isInitialized, setLocale]
  );

  // Gate children on locale resolution. Unlike the web `LocaleProvider` (where
  // `i18n.language` is available synchronously and `isInitialized` is always
  // true), native resolves the persisted locale ASYNCHRONOUSLY from AsyncStorage.
  // If we mounted the app tree during that window, screens would fire API
  // requests under the transient `en` default (no `x-locale`), and the resulting
  // English translate-on-read (Tier-B) payloads would race — and often beat — the
  // correct refetch once the locale settles, leaving non-EN users on English
  // CheckUp/QultureUp/etc. content. Deferring children until `isInitialized`
  // eliminates the window entirely: the FIRST request already carries the right
  // `x-locale`. `init()` always flips `isInitialized` (even on error), so this
  // can never wedge. The native splash stays up over the brief gap.
  return (
    <NativeLocaleContext.Provider value={value}>
      <I18nextProvider i18n={i18n}>{isInitialized ? children : null}</I18nextProvider>
    </NativeLocaleContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Hook to access native locale context
 *
 * @example
 * ```tsx
 * const { locale, setLocale, isLoading } = useNativeLocaleContext();
 * ```
 */
export function useNativeLocaleContext(): LocaleContextType {
  const context = useContext(NativeLocaleContext);

  if (!context) {
    throw new Error('useNativeLocaleContext must be used within a NativeLocaleProvider');
  }

  return context;
}

export { NativeLocaleContext };
